import type { ThreatOverlayPoint } from '@/store/overlayDataStore';

export type { ThreatOverlayPoint };

// ---------------------------------------------------------------------------
// Adapter interface
// ---------------------------------------------------------------------------

export interface ThreatOverlayAdapter {
  /** Unique identifier — must match LayerId in layerStore */
  readonly id: string;
  /** Human-readable label shown in the layer panel */
  readonly label: string;
  /** Lower = higher trust. DarkAPI (first-party) = 1, third-party = 5+ */
  readonly priority: number;
  /**
   * Fetch threat overlay points for the given targets.
   * Targets are domain names or IP addresses from the current graph.
   * Returns empty array if the adapter is unconfigured or backend is unavailable.
   */
  fetch(targets: string[]): Promise<ThreatOverlayPoint[]>;
  /** Optional: dispose any resources (AbortControllers, timers) */
  dispose?(): void;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

class Registry {
  private readonly _adapters = new Map<string, ThreatOverlayAdapter>();

  register(adapter: ThreatOverlayAdapter): void {
    this._adapters.set(adapter.id, adapter);
  }

  get(id: string): ThreatOverlayAdapter | undefined {
    return this._adapters.get(id);
  }

  /** Returns all adapters sorted by priority ascending (lowest = highest trust) */
  all(): ThreatOverlayAdapter[] {
    return [...this._adapters.values()].sort((a, b) => a.priority - b.priority);
  }
}

export const AdapterRegistry = new Registry();

/** Register all built-in adapters. Call once at app bootstrap. */
export async function initializeAdapters(): Promise<void> {
  const { DarkAPIAdapter } = await import('./DarkAPIAdapter');
  AdapterRegistry.register(DarkAPIAdapter);
}
